import { useEffect, useRef } from 'react';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';

export const ServiceMap = ({ 
  serviceType, 
  serviceConfig,
  servicePoints = [], 
  isVisible = true,
  onMarkerClick 
}) => {
  const markersRef = useRef([]);
  const clustererRef = useRef(null);
  const infoWindowsRef = useRef([]);
  
  const config = serviceConfig || {
    mapId: `map-wrapper-${serviceType.toLowerCase().replace(/[^a-z0-9]/g, '-')}`,
    color: '#4ECDC4',
    clusterColor: '#4ECDC4'
  };
  
  const { map, isLoaded } = useGoogleMaps(config.mapId);

  useEffect(() => {
    const mapElement = document.getElementById(config.mapId);
    if (mapElement) {
      mapElement.style.display = isVisible ? 'block' : 'none';
    }
  }, [isVisible, config.mapId]);

  // Exponer función para centrar el mapa en un punto
  useEffect(() => {
    if (!map || !config.mapId) return;

    if (!window.mapCenterFunctions) {
      window.mapCenterFunctions = {};
    }

    window.mapCenterFunctions[config.mapId] = (point) => {
      if (!map || !point || !point.lat || !point.lng) return;

      const position = { lat: point.lat, lng: point.lng };
      
      // Cerrar todos los InfoWindows abiertos
      infoWindowsRef.current.forEach(iw => {
        if (iw) iw.close();
      });

      // Centrar el mapa
      map.setCenter(position);
      map.setZoom(15);

      // Encontrar y abrir el InfoWindow del marcador correspondiente
      const marker = markersRef.current.find(m => {
        if (!m || !m.getPosition) return false;
        const pos = m.getPosition();
        return pos.lat() === point.lat && pos.lng() === point.lng;
      });

      if (marker) {
        const infoWindow = infoWindowsRef.current.find(iw => {
          return iw && iw.marker === marker;
        });

        if (infoWindow) {
          infoWindow.open({
            anchor: marker,
            map: map,
            shouldFocus: false
          });
        }
      }
    };

    return () => {
      if (window.mapCenterFunctions && window.mapCenterFunctions[config.mapId]) {
        delete window.mapCenterFunctions[config.mapId];
      }
    };
  }, [map, config.mapId]);

  useEffect(() => {
    if (!map || !isLoaded) return;

    // Limpiar marcadores anteriores
    markersRef.current.forEach(marker => {
      if (marker && marker.setMap) {
        marker.setMap(null);
      }
    });
    markersRef.current = [];

    // Cerrar todos los InfoWindows
    infoWindowsRef.current.forEach(iw => {
      if (iw && iw.close) {
        iw.close();
      }
    });
    infoWindowsRef.current = [];

    // Limpiar clusterer anterior
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current = null;
    }

    if (servicePoints.length === 0) {
      console.error('[DEBUG] ServiceMap: No servicePoints to render');
      return;
    }

    console.error('[DEBUG] ServiceMap: Rendering', servicePoints.length, 'points');
    console.error('[DEBUG] ServiceMap: First point:', servicePoints[0]);

    // Crear nuevos marcadores con círculos de color
    const markers = servicePoints.map(point => {
      try {
        // Usar el color del servicio (prioridad: config.color > config.clusterColor > default)
        const markerColor = config.color || config.clusterColor || '#4ECDC4';
        console.error('[DEBUG] ServiceMap: Creating marker with color:', markerColor, 'for service:', serviceType);
        const icon = {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: markerColor,
          fillOpacity: 0.8,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        };

        const marker = new window.google.maps.Marker({
          position: { lat: point.lat, lng: point.lng },
          map: null,
          title: point.title || '',
          icon: icon,
        });

        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div class="info-box">
              ${config.icon ? `<img class="projimg" src="${config.icon}" alt=""/>` : ''}
              <p class="projname">${point.title || 'Sin nombre'}</p>
              ${point.address ? `<p class="projdesc">${point.address}</p>` : ''}
            </div>
          `
        });

        infoWindow.marker = marker;
        infoWindowsRef.current.push(infoWindow);

        marker.addListener('click', () => {
          infoWindowsRef.current.forEach(iw => {
            if (iw && iw !== infoWindow) {
              iw.close();
            }
          });

          infoWindow.open({
            anchor: marker,
            map: map,
            shouldFocus: false
          });

          if (onMarkerClick) {
            onMarkerClick(point);
          }
        });

        if (map && typeof map.setCenter === 'function') {
          marker.setMap(map);
        }

        return marker;
      } catch (error) {
        return null;
      }
    }).filter(marker => marker !== null);

    markersRef.current = markers;

    // Crear clusterer si está disponible
    if (window.markerClusterer && markers.length > 0) {
      try {
        // Usar el mismo color del servicio para los clusters
        const clusterColor = config.color || config.clusterColor || '#4ECDC4';
        console.error('[DEBUG] ServiceMap: Creating clusterer with color:', clusterColor, 'for service:', serviceType);
        clustererRef.current = new window.markerClusterer.MarkerClusterer({
          map: map,
          markers: markers,
          renderer: {
            render({ count, position }) {
              return new window.google.maps.Marker({
                position,
                label: {
                  text: String(count),
                  color: "black",
                  fontWeight: "bold",
                },
                icon: {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: Math.max(20, count / 10),
                  fillColor: clusterColor,
                  fillOpacity: 0.7,
                  strokeWeight: 0,
                },
              });
            },
          },
          gridSize: 100
        });
      } catch (error) {
        // Error silencioso
      }
    }

    // Ajustar bounds si hay marcadores
    if (markers.length > 0) {
      try {
        const bounds = new window.google.maps.LatLngBounds();
        markers.forEach(marker => {
          if (marker && marker.getPosition) {
            bounds.extend(marker.getPosition());
          }
        });
        
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds);
        }
      } catch (error) {
        // Error silencioso
      }
    }
  }, [map, isLoaded, servicePoints, config, onMarkerClick]);

  return (
    <div 
      id={config.mapId}
      style={{ 
        height: '100%', 
        maxHeight: '500px',
        width: '100%',
        borderRadius: '8px',
        display: isVisible ? 'block' : 'none'
      }} 
    />
  );
};
